import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownTrayIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useAdminPeople, useAdminPhotos } from '../../hooks/useQuestAdmin';
import { currentQuarter, downloadCsv } from './questShared';
import { Avatar, BTN_SECONDARY, Card, INPUT, Loading } from './adminUi';

// Everyone, with their points. The list is the interface; search narrows it —
// the person to award may not have scored yet, or you may not know how their
// name is spelt.
function PeopleTab() {
  const q = useAdminPeople();
  const [search, setSearch] = useState('');
  const people = useMemo(() => {
    const s = search.trim().toLowerCase();
    const all = q.data || [];
    return s ? all.filter((p) => [p.display_name, p.job_title, p.region, p.cluster]
      .some((v) => (v || '').toLowerCase().includes(s))) : all;
  }, [q.data, search]);
  // photos for what is on screen: the first 75 of the list, or of a search
  const photos = useAdminPhotos(people.slice(0, 75).map((p) => p.entra_user_id));

  const exportCsv = () => downloadCsv(`nano-quest-people-${currentQuarter()}.csv`, [
    ['Name', (p) => p.display_name],
    ['Job title', (p) => p.job_title],
    ['Department', (p) => p.department],
    ['Region', (p) => p.region],
    ['Cluster', (p) => p.cluster],
    ['Position this quarter', (p) => p.position],
    ['Points this quarter', (p) => p.points_quarter],
    ['Points all time', (p) => p.points_lifetime],
  ], q.data || []);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Narrow by name, job or region"
            className={`${INPUT} pl-9`} />
        </div>
        <button type="button" onClick={exportCsv} disabled={!q.data?.length} className={BTN_SECONDARY}>
          <ArrowDownTrayIcon className="h-4 w-4" /> Export CSV
        </button>
      </div>
      <Card>
        <Loading q={q} what="people" />
        {q.data && !people.length ? <p className="px-4 py-6 text-center text-sm text-gray-400">Nobody matches that.</p> : null}
        <ul className="divide-y divide-gray-100">
          {people.map((p) => (
            <li key={p.entra_user_id}>
              <Link to={`/quest/people/${p.entra_user_id}`}
                className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-gray-50">
                <Avatar name={p.display_name} photo={photos[p.entra_user_id]} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-gray-900">{p.display_name}</span>
                  <span className="block truncate text-xs text-gray-400">
                    {[p.job_title, p.region, p.cluster].filter(Boolean).join(' · ') || '—'}
                  </span>
                </span>
                <span className="shrink-0 text-right">
                  <span className="block text-sm font-bold tabular-nums text-gray-900">{p.points_quarter}</span>
                  <span className="block text-xs text-gray-400">
                    {p.position ? `#${p.position} this quarter` : 'no points this quarter'}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

export default PeopleTab;
